import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  FileText,
  Network,
  Pencil,
  Sparkles,
  UploadCloud,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FilesUploadButton } from '@/components/files-upload-button';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { useTenant } from '@/hooks/use-tenant';
import { cn } from '@/lib/utils';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { getMessageByStatus } from '@/lib/error-message';
import { toast } from 'sonner';
import {
  useBulkCreateSkillFrameworkMutation,
  useParseSkillsFrameworkMutation,
  type ParsedSkillFrameworkNode,
} from '@/state/skills/skills-api';
import type { SkillNode } from './components/skill-framework-chart-utils';
import {
  normalizeSkillDescription,
  transformDataForTree,
  type SkillTreeNodeData,
} from './components/skill-framework-chart-utils';
import SkillFrameworkSunburst from './components/skill-framework-sunburst';
import SkillFrameworkTree from './components/skill-framework-tree';

interface LocalSkillNode {
  id: number;
  name: string;
  description: string;
  children: LocalSkillNode[];
}

interface BulkPayloadNode {
  name: string;
  description: string;
  skill_type: 'Skill/Competence';
  children: BulkPayloadNode[];
}

const pickRootFrameworks = (nodes: ParsedSkillFrameworkNode[]) => {
  const levelZero = nodes.filter((node) => node.level === 0);
  if (levelZero.length) return levelZero;
  const topParents = nodes.filter(
    (node) => (node.parent === null || typeof node.parent === 'undefined') && (node.children?.length ?? 0) > 0
  );
  if (topParents.length) return topParents;
  return nodes.length ? [nodes[0]] : [];
};

const buildLocalNodes = (nodes: ParsedSkillFrameworkNode[]) => {
  const roots = pickRootFrameworks(nodes);
  let counter = 1;

  const toLocalNode = (node: ParsedSkillFrameworkNode): LocalSkillNode => ({
    id: counter++,
    name: node.name?.trim() || 'Untitled skill',
    description: node.description || '',
    children: (node.children || []).map(toLocalNode),
  });

  return roots.map(toLocalNode);
};

const toSkillNode = (node: LocalSkillNode, level = 0): SkillNode => ({
  id: node.id,
  name: node.name,
  description: node.description,
  published: false,
  level,
  skill_type: null,
  children: node.children.map((child) => toSkillNode(child, level + 1)),
});

const updateNodeById = (
  node: LocalSkillNode,
  nodeId: number,
  updater: (target: LocalSkillNode) => LocalSkillNode
): LocalSkillNode => {
  if (node.id === nodeId) {
    return updater(node);
  }

  return {
    ...node,
    children: node.children.map((child) => updateNodeById(child, nodeId, updater)),
  };
};

const findLocalNode = (node: LocalSkillNode, nodeId: number): LocalSkillNode | null => {
  if (node.id === nodeId) return node;
  for (const child of node.children) {
    const found = findLocalNode(child, nodeId);
    if (found) return found;
  }
  return null;
};

const buildBulkPayload = (nodes: LocalSkillNode[]) => {
  const toPayloadNode = (node: LocalSkillNode): BulkPayloadNode => ({
    name: node.name,
    description: node.description,
    skill_type: 'Skill/Competence',
    children: node.children.map(toPayloadNode),
  });

  return { skill_frameworks: nodes.map(toPayloadNode) };
};

const findNodeById = (node: SkillTreeNodeData | null, nodeId: string): SkillTreeNodeData | null => {
  if (!node) return null;
  if (String(node.id) === nodeId) return node;
  for (const child of node.children || []) {
    const found = findNodeById(child, nodeId);
    if (found) return found;
  }
  return null;
};

const getSkillTypeLabel = () => 'Skill/Competence';

const GeneratedHierarchyDrawer = ({
  treeData,
  height,
  collapsedMap,
  onToggle,
  onView,
  onEdit,
}: {
  treeData: SkillTreeNodeData | null;
  height: number | string;
  collapsedMap: Record<string, boolean>;
  onToggle: (nodeId: string) => void;
  onView: (nodeId: string) => void;
  onEdit: (nodeId: string) => void;
}) => {
  const headerNode = treeData;
  const headerDescription = normalizeSkillDescription(headerNode?.description);

  if (!headerNode) {
    return (
      <aside
        className="border-l bg-muted/40"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      />
    );
  }

  const renderNode = (node: SkillTreeNodeData, depth: number) => {
    const hasChildren = !!node.children?.length;
    const isCollapsed = collapsedMap[node.id] ?? false;

    return (
      <div key={node.id}>
        <div
          className="group flex items-center justify-between gap-2 py-1.5"
          style={{ paddingLeft: `${depth * 16}px` }}
        >
          <div className="flex items-start gap-2 min-w-0">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onToggle(node.id)}
                className="mt-0.5 h-5 w-5 rounded-sm inline-flex items-center justify-center hover:bg-accent shrink-0"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5 text-emerald-500" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-emerald-500" />
                )}
              </button>
            ) : (
              <span className="mt-1 h-5 w-5 inline-flex items-center justify-center shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              </span>
            )}

            <div className="min-w-0">
              <p
                className={cn(
                  'truncate text-sm leading-5',
                  hasChildren ? 'text-foreground font-medium' : 'text-muted-foreground'
                )}
                title={node.name}
              >
                {node.name}
              </p>
              <p className="text-xs text-muted-foreground leading-4">
                {getSkillTypeLabel()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button
              type="button"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
              onClick={() => onView(node.id)}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
              onClick={() => onEdit(node.id)}
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        </div>

        {hasChildren && !isCollapsed && (
          <div>
            {node.children!.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className="border-l bg-muted/30 overflow-y-auto"
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
    >
      <div className="sticky top-0 z-20 bg-muted/40 border-b border-border px-3 pt-8 pb-3 flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="text-base font-medium truncate">{headerNode.name}</h3>
          {headerDescription && (
            <p className="text-xs text-muted-foreground line-clamp-2">{headerDescription}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
            onClick={() => onView(headerNode.id)}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
            onClick={() => onEdit(headerNode.id)}
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-0.5 px-3 pb-3">
        {(headerNode.children || []).map((child) => renderNode(child, 0))}
      </div>
    </aside>
  );
};

const GeneratedHierarchySheet = ({
  node,
  depth,
  onClose,
  onView,
  onEdit,
}: {
  node: SkillTreeNodeData;
  depth: number;
  onClose: () => void;
  onView: (nodeId: string) => void;
  onEdit: (nodeId: string) => void;
}) => {
  const width = depth === 0 ? 360 : 340;
  const rightOffset = depth * 28;
  const description = normalizeSkillDescription(node.description);

  return (
    <Sheet open onOpenChange={(open) => (!open ? onClose() : null)}>
      <SheetContent
        side="right"
        hideOverlay
        showClose={false}
        className="bg-transparent p-0 shadow-none border-0"
        style={{ width, maxWidth: '90vw', right: `${rightOffset}px` }}
      >
        <div className="rounded-xl border border-border bg-background/95 shadow-xl overflow-hidden">
          <SheetHeader className="border-b border-border pt-6 px-4 pb-4">
            <SheetTitle className="text-base">{node.name}</SheetTitle>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </SheetHeader>

          <div className="px-4 py-3 space-y-3">
            <div className="flex items-center justify-between border-b border-border/60 pb-2">
              <p className="text-sm font-medium text-muted-foreground">Sub categories</p>
              <button
                type="button"
                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                onClick={() => onEdit(node.id)}
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
            {node.children?.length ? (
              node.children.map((child) => (
                <div
                  key={child.id}
                  className="flex items-start justify-between gap-2 border-b border-border/60 pb-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{child.name}</p>
                    <p className="text-xs text-muted-foreground">{getSkillTypeLabel()}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                      onClick={() => onView(child.id)}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                      onClick={() => onEdit(child.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No sub-categories available.</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const SkillsAndCompetencesGenerate = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { isSchoolAdmin, isTeacher, hasAIModule, isLoading: permissionsLoading } = useClientPermissions();
  const canAccess = (isSchoolAdmin || isTeacher) && hasAIModule;

  const [parseSkillsFramework, { isLoading: isParsing }] = useParseSkillsFrameworkMutation();
  const [bulkCreateSkillFramework, { isLoading: isSaving }] = useBulkCreateSkillFrameworkMutation();

  const [frameworks, setFrameworks] = useState<LocalSkillNode[]>([]);
  const [activeFrameworkId, setActiveFrameworkId] = useState<number | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [view, setView] = useState<'sunburst' | 'tree'>('sunburst');
  const [editNodeId, setEditNodeId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [sourceFileName, setSourceFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const activeFramework = frameworks.find((framework) => framework.id === activeFrameworkId) || frameworks[0];
  const activeSkillNode = useMemo(() => (activeFramework ? toSkillNode(activeFramework) : null), [activeFramework]);
  const treeData = useMemo(() => (activeSkillNode ? transformDataForTree(activeSkillNode) : null), [activeSkillNode]);
  const previewUrl = useMemo(() => (selectedFile ? URL.createObjectURL(selectedFile) : null), [selectedFile]);

  const chartHeight = 'calc(100vh - 260px)';

  const handleUpload = async (files: FileList) => {
    const file = files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setSourceFileName(file.name);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClearFile = () => {
    setSelectedFile(null);
    setSourceFileName(null);
  };

  const handleGenerateFramework = async () => {
    if (!selectedFile || !tenant || isParsing) return;
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await parseSkillsFramework({ tenant, data: formData }).unwrap();
      const parsed = buildLocalNodes(response.skills_framework || []);

      if (!parsed.length) {
        toast.error('No skills detected', {
          description: 'Try uploading a more detailed framework document.',
        });
        return;
      }

      setFrameworks(parsed);
      setActiveFrameworkId(parsed[0].id);
      setSelectedNodeIds([]);
      setCollapsedMap({});
      toast.success('Framework generated', {
        description: 'Review and edit the skills before saving.',
      });
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Failed to parse framework', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  const handleEditOpen = (nodeId: string) => {
    if (!activeFramework) return;
    const localNode = findLocalNode(activeFramework, Number(nodeId));
    if (!localNode) return;
    setEditNodeId(localNode.id);
    setEditName(localNode.name);
    setEditDescription(localNode.description);
  };

  const handleEditSave = () => {
    if (editNodeId === null) return;
    setFrameworks((prev) =>
      prev.map((framework) =>
        updateNodeById(framework, editNodeId, (target) => ({
          ...target,
          name: editName.trim() || target.name,
          description: editDescription,
        }))
      )
    );
    setEditNodeId(null);
  };

  const handleSaveFramework = async () => {
    if (!tenant || !frameworks.length || isSaving) return;

    try {
      const response = await bulkCreateSkillFramework({
        tenant,
        data: buildBulkPayload(frameworks),
      }).unwrap();

      const createdId = response.created_frameworks?.[0]?.id;
      toast.success(response.message || 'Framework saved');
      if (createdId) {
        navigate(`/${tenant}/admin-tools/skills-and-competences/${createdId}`);
      }
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Failed to save framework', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        You do not have permission to generate skills frameworks.
      </div>
    );
  }

  if (!activeFramework || !activeSkillNode) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-6 md:px-6">
        <div className="rounded-3xl border border-border/50 bg-gradient-to-br from-slate-50 via-white to-blue-50 p-6 shadow-sm dark:from-slate-900 dark:via-slate-900/80 dark:to-slate-900/40">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm text-muted-foreground">AI Skills Studio</p>
              <h1 className="text-3xl font-bold tracking-tight">Generate a Skills Framework</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Upload a PDF and let AI map the competencies into a structured framework.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        </div>

        <section className="grid gap-4 md:grid-cols-2">
          {[
            {
              title: 'AI-Powered Framework Generation',
              description:
                'Transform documents into a structured skills taxonomy with AI-assisted extraction and hierarchy building.',
              icon: Sparkles,
              tint: 'from-blue-500/15 to-blue-500/5 text-blue-700 dark:text-blue-300',
            },
            {
              title: 'Competency Mapping',
              description:
                'Identify skills, competencies, and proficiency levels to create a consistent structure for assessment.',
              icon: Network,
              tint: 'from-violet-500/15 to-violet-500/5 text-violet-700 dark:text-violet-300',
            },
            {
              title: 'Learning Outcomes Alignment',
              description:
                'Align skill clusters with outcomes to ensure clear progression pathways and measurable goals.',
              icon: FileText,
              tint: 'from-emerald-500/15 to-emerald-500/5 text-emerald-700 dark:text-emerald-300',
            },
            {
              title: 'Structured Organization',
              description:
                'Keep frameworks neatly organized into categories, subcategories, and descriptions.',
              icon: UploadCloud,
              tint: 'from-amber-500/15 to-amber-500/5 text-amber-700 dark:text-amber-300',
            },
          ].map((card) => (
            <Card
              key={card.title}
              className="relative overflow-hidden border border-border/60 bg-background/70 p-5 shadow-sm backdrop-blur-sm"
            >
              <div className={cn('absolute inset-0 bg-gradient-to-br opacity-60', card.tint)} />
              <div className="relative flex gap-3">
                <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-background/90 shadow-sm">
                  <card.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">{card.title}</h3>
                  <p className="text-sm text-muted-foreground">{card.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </section>

        <section className="rounded-3xl border border-border/50 bg-card/70 p-6 shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Upload Framework Document</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Only PDF files are supported. Maximum file size 30MB.
          </p>
          <div className="mt-4">
            {!selectedFile ? (
              <FilesUploadButton
                onFilesSelect={handleUpload}
                accept=".pdf"
                disabled={isParsing}
                title={isParsing ? 'Analyzing document...' : 'Click or drag a PDF file to upload'}
                subtitle={isParsing ? 'Parsing the framework with AI' : 'Supported: PDF only'}
                className="min-h-[190px] w-full"
              />
            ) : (
              <div className="min-h-[190px] w-full rounded-xl border-2 border-dashed border-muted-foreground/30 bg-background/70 p-5">
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <button
                    type="button"
                    className="flex items-center gap-3 rounded-xl border border-border/60 bg-background/90 px-4 py-3 shadow-sm transition hover:border-primary/40 hover:shadow-md"
                    onClick={() => {
                      if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/40">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB · Click to preview
                      </p>
                    </div>
                  </button>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button onClick={handleGenerateFramework} disabled={isParsing} className="gap-2">
                      {isParsing ? 'Generating...' : 'Generate Framework'}
                    </Button>
                    <Button variant="outline" onClick={handleClearFile} disabled={isParsing}>
                      Clear file
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  const sheetStack = selectedNodeIds;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 md:px-6">
      <Alert className="border-blue-200/70 bg-blue-50/70 text-blue-900 dark:border-blue-500/30 dark:bg-blue-950/40 dark:text-blue-100">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Important: Save Your Framework!</AlertTitle>
        <AlertDescription>
          Your generated framework will be lost if you refresh the page. Use the "Save Framework" button to persist it.
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-fit gap-2"
            onClick={() => {
              setFrameworks([]);
              setActiveFrameworkId(null);
              setSelectedNodeIds([]);
              setSourceFileName(null);
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Upload
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Generated Skills Framework</h1>
            {sourceFileName && (
              <p className="text-sm text-muted-foreground">Source: {sourceFileName}</p>
            )}
          </div>
        </div>
        <Button onClick={handleSaveFramework} disabled={isSaving} className="gap-2">
          {isSaving ? 'Saving...' : 'Save Framework'}
        </Button>
      </div>

      {frameworks.length > 1 ? (
        <div className="max-w-sm">
          <Select
            value={String(activeFrameworkId ?? '')}
            onValueChange={(value) => {
              setActiveFrameworkId(Number(value));
              setSelectedNodeIds([]);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a framework" />
            </SelectTrigger>
            <SelectContent>
              {frameworks.map((framework) => (
                <SelectItem key={framework.id} value={String(framework.id)}>
                  {framework.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="relative">
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="absolute top-2 right-2 z-40 h-7 w-7"
          onClick={() => setIsDrawerOpen((prev) => !prev)}
        >
          {isDrawerOpen ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </Button>

        <div
          className={cn(
            'grid gap-0 transition-all',
            isDrawerOpen ? 'grid-cols-[minmax(0,1fr)_360px]' : 'grid-cols-1'
          )}
        >
          <div className="relative min-w-0 overflow-hidden">
            <Tabs value={view} onValueChange={(value) => setView(value as 'sunburst' | 'tree')} className="space-y-0">
              <div className="absolute top-3 left-3 z-20">
                <TabsList>
                  <TabsTrigger value="sunburst">Sunburst</TabsTrigger>
                  <TabsTrigger value="tree">Tree</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="sunburst" className="mt-0">
                <SkillFrameworkSunburst framework={activeSkillNode} height={chartHeight} />
              </TabsContent>

              <TabsContent value="tree" className="mt-0">
                {view === 'tree' && <SkillFrameworkTree framework={activeSkillNode} height={chartHeight} />}
              </TabsContent>
            </Tabs>
          </div>

          {isDrawerOpen && (
            <GeneratedHierarchyDrawer
              treeData={treeData}
              height={chartHeight}
              collapsedMap={collapsedMap}
              onToggle={(nodeId) =>
                setCollapsedMap((prev) => ({
                  ...prev,
                  [nodeId]: !prev[nodeId],
                }))
              }
              onView={(nodeId) => {
                setSelectedNodeIds([String(nodeId)]);
                if (!isDrawerOpen) setIsDrawerOpen(true);
              }}
              onEdit={handleEditOpen}
            />
          )}
        </div>
      </div>

      {sheetStack.map((nodeId, index) => {
        const node = findNodeById(treeData, nodeId);
        if (!node) return null;
        return (
          <GeneratedHierarchySheet
            key={`${node.id}-${index}`}
            node={node}
            depth={index}
            onEdit={handleEditOpen}
            onClose={() => setSelectedNodeIds((prev) => prev.slice(0, index))}
            onView={(childId) =>
              setSelectedNodeIds((prev) => {
                const nextId = String(childId);
                const base = prev.slice(0, index + 1);
                if (base[base.length - 1] === nextId) return base;
                return [...base, nextId];
              })
            }
          />
        );
      })}

      <Dialog
        open={editNodeId !== null}
        onOpenChange={(open) => {
          if (!open) setEditNodeId(null);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Edit Skill Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input value={editName} onChange={(event) => setEditName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
                className="min-h-[120px]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditNodeId(null)}>
                Cancel
              </Button>
              <Button onClick={handleEditSave}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillsAndCompetencesGenerate;
